import { useEditableForm } from "@/stores/useEditableFormStore";
import { cn } from "@/lib/utils/cn";
import React, { useCallback, useContext, useId, useRef, useState } from "react";
import { Eye, EyeOff, X } from "lucide-react";
import { AIGeneratedBadge } from "./AIGeneratedBadge";
import { LabelValueDefaultsContext } from "@/components/common/ui/LabelValue";
import { Typography } from "@/components/common/ui/Typography";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  ref?: React.Ref<HTMLInputElement>;
  label?: string;
  error?: string;
  helperText?: React.ReactNode;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  formId?: string;
  isAIInferred?: boolean;
  aiInferredReason?: string;
  inferredBadge?: React.ReactNode;
  onFieldClick?: () => void;
  rightButton?: React.ReactNode;
  inputSize?: "default" | "sm" | "xs";
  /**
   * Controls the show/hide password toggle button.
   * - Auto-enabled when `type="password"` (default).
   * - Set `false` to opt out (e.g. when the consumer provides their own `rightButton`).
   * - Explicitly setting `true` on a non-password input has no effect.
   */
  showPasswordToggle?: boolean;
  /** Shows an X button to clear the input when it has a value. */
  clearable?: boolean;
  /** Called when clear is clicked. If omitted, fires a synthetic onChange with empty string. */
  onClear?: () => void;
  /**
   * Controls whether the native number-input spin buttons (up/down arrows) are
   * rendered. Only takes effect when `type="number"`. Defaults to `true` —
   * the spin buttons are hidden by default since they're rarely desired. Pass
   * `false` to opt back in.
   */
  hideSpinButtons?: boolean;
  /** Horizontal alignment of the input text. Defaults to the surrounding
   * `LabelValueDefaultsContext` value, or "left" when none is provided. */
  textAlign?: "left" | "center" | "right";
}

const sizeStyles = {
  default: "px-3 py-[9px] text-sm rounded-lg",
  sm: "px-2.5 py-1.5 text-sm rounded-lg h-9",
  xs: "px-2 py-1.5 text-xs rounded-lg h-7",
} as const;

const textAlignClass = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
} as const;

const iconConfig = {
  default: { leftPad: "pl-10", rightPad: "pr-10", leftPos: "left-3", rightPos: "right-3" },
  sm: { leftPad: "pl-9", rightPad: "pr-9", leftPos: "left-2.5", rightPos: "right-2.5" },
  xs: { leftPad: "pl-7", rightPad: "pr-7", leftPos: "left-2", rightPos: "right-2" },
} as const;

export function Input({
  ref,
  label,
  error,
  helperText,
  leftIcon,
  rightIcon,
  formId,
  className = "",
  id,
  name,
  required,
  disabled,
  value,
  defaultValue,
  isAIInferred,
  aiInferredReason,
  inferredBadge,
  onFieldClick,
  rightButton,
  inputSize = "default",
  showPasswordToggle,
  clearable,
  onClear,
  hideSpinButtons = true,
  textAlign,
  type,
  onChange,
  ...props
}: InputProps) {
  const generatedId = useId();
  const fieldId = id || name || generatedId;
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const labelValueDefaults = useContext(LabelValueDefaultsContext);
  const effectiveTextAlign =
    textAlign ?? labelValueDefaults.valueAlign ?? "left";

  // Internal ref for programmatic clear; merged with consumer ref
  const internalRef = useRef<HTMLInputElement>(null);
  const mergedRef = useCallback(
    (node: HTMLInputElement | null) => {
      (internalRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
      }
    },
    [ref],
  );

  // Track value for uncontrolled inputs so we know when to show the clear button
  const [internalValue, setInternalValue] = useState((defaultValue as string) ?? "");
  const hasValue = value !== undefined ? String(value).length > 0 : internalValue.length > 0;
  const shouldShowClearButton = clearable && hasValue && !disabled;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (value === undefined) setInternalValue(e.target.value);
      onChange?.(e);
    },
    [value, onChange],
  );

  const handleClear = useCallback(() => {
    if (onClear) {
      onClear();
      return;
    }
    const input = internalRef.current;
    if (!input) return;
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      "value",
    )?.set;
    if (nativeInputValueSetter) {
      nativeInputValueSetter.call(input, "");
      input.dispatchEvent(new Event("input", { bubbles: true }));
    }
    if (value === undefined) setInternalValue("");
    input.focus();
  }, [onClear, value]);

  // Auto-enable the toggle for password inputs, unless explicitly disabled or
  // the consumer is providing their own right-side content.
  const isPasswordInput = type === "password";
  const shouldShowPasswordToggle =
    isPasswordInput &&
    (showPasswordToggle ?? true) &&
    !rightIcon &&
    !rightButton;
  const effectiveType =
    shouldShowPasswordToggle && isPasswordVisible && !disabled ? "text" : type;
  const passwordIconSize = inputSize === "default" ? "w-5 h-5" : "w-4 h-4";
  const passwordToggleButton = shouldShowPasswordToggle ? (
    <button
      type="button"
      disabled={disabled}
      onClick={() => setIsPasswordVisible((v) => !v)}
      aria-label={isPasswordVisible ? "Hide password" : "Show password"}
      aria-pressed={isPasswordVisible}
      title={isPasswordVisible ? "Hide password" : "Show password"}
      className="text-gray-400 hover:text-gray-600 transition-colors"
    >
      {isPasswordVisible ? (
        <EyeOff className={passwordIconSize} />
      ) : (
        <Eye className={passwordIconSize} />
      )}
    </button>
  ) : null;
  const clearIconSize = inputSize === "default" ? "w-4 h-4" : "w-3.5 h-3.5";
  const clearButton = shouldShowClearButton ? (
    <button
      type="button"
      disabled={disabled}
      onClick={handleClear}
      aria-label="Clear input"
      title="Clear"
      className="text-gray-400 hover:text-gray-600 transition-colors"
    >
      <X className={clearIconSize} />
    </button>
  ) : null;
  const effectiveRightButton = rightButton ?? passwordToggleButton ?? clearButton;
  const {
    forms,
    isEditing: globalIsEditing,
    editableMode: globalEditableMode,
  } = useEditableForm();

  const isEditing = formId
    ? (forms[formId]?.isEditing ?? false)
    : globalIsEditing;

  const editableMode = formId
    ? (forms[formId]?.editableMode ?? false)
    : globalEditableMode;

  const displayValue = value ?? defaultValue;

  // Check if field is empty and required
  const isEmpty = !displayValue || displayValue === "";
  const isRequiredEmpty = isEmpty && required;

  if (editableMode && !isEditing) {
    return (
      <div className="w-full">
        {label && (
          <Typography as="label" variant="body" weight="medium" color="text-navigator-text-primary" className="inline-flex items-center gap-1.5 mb-2">
            {label}
            {isAIInferred &&
              (inferredBadge || <AIGeneratedBadge reason={aiInferredReason} />)}
          </Typography>
        )}
        <div
          className={cn(
            "text-gray-900 bg-gray-50 border rounded-lg",
            sizeStyles[inputSize],
            isRequiredEmpty ? "border-red-300 bg-red-100/50" : "border-gray-200",
            onFieldClick && "cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors",
          )}
          onClick={onFieldClick}
          role={onFieldClick ? "button" : undefined}
          tabIndex={onFieldClick ? 0 : undefined}
          onKeyDown={
            onFieldClick
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") onFieldClick();
                }
              : undefined
          }
        >
          {displayValue || "—"}
        </div>
      </div>
    );
  }

  const icons = iconConfig[inputSize];

  return (
    <div className="w-full">
      {label && (
        <Typography as="label" htmlFor={fieldId} variant="body" weight="medium" color="text-navigator-text-primary" className="block mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Typography>
      )}

      <div className="relative">
        {leftIcon && (
          <div className={cn("absolute top-1/2 transform -translate-y-1/2 text-gray-400", icons.leftPos)}>
            {leftIcon}
          </div>
        )}
        <input
          ref={mergedRef}
          id={fieldId}
          name={name}
          type={effectiveType}
          required={required}
          disabled={disabled}
          onChange={handleChange}
          {...(value !== undefined ? { value } : { defaultValue })}
          className={cn(
            "w-full border transition-colors",
            sizeStyles[inputSize],
            textAlignClass[effectiveTextAlign],
            leftIcon && icons.leftPad,
            (rightIcon || effectiveRightButton || clearable) && icons.rightPad,
            error
              ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
              : isRequiredEmpty
                ? "border-red-300 bg-red-100/50 focus:border-red-500 focus:ring-red-500/20"
                : "border-gray-200 focus:ring-navigator-primary focus:border-transparent",
            "focus:outline-none focus:ring-2",
            "bg-white text-navigator-text-primary",
            "placeholder:text-gray-400",
            "disabled:bg-gray-50 disabled:cursor-not-allowed",
            type === "number" && hideSpinButtons &&
              "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0",
            className,
          )}
          {...props}
        />
        {rightIcon && (
          <div className={cn("absolute top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none", icons.rightPos)}>
            {rightIcon}
          </div>
        )}
        {effectiveRightButton && !rightIcon && (
          <div className={cn("absolute top-1/2 transform -translate-y-1/2 text-gray-400 leading-0", icons.rightPos)}>
            {effectiveRightButton}
          </div>
        )}
      </div>

      {error && <Typography as="p" variant="body" color="error" className="mt-1.5">{error}</Typography>}
      {helperText && !error && (
        <Typography as="p" variant="body" color="light" className="mt-1.5">{helperText}</Typography>
      )}
    </div>
  );
}
